import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { OfferTemplate } from '@/features/webinar/types';
import { useAppDispatch } from '@/app/hooks';
import { postAnalyticsEvent } from '@/features/live/liveThunks';
import { useWebinarT } from '@/hooks/useWebinarT';

export default function OfferThankYou({
  visualIdentity,
  offer,
  webinarId,
  sessionIdentifier,
  authorization,
}: {
  visualIdentity: WorkspaceVisualIdentity;
  offer?: OfferTemplate | null;
  webinarId?: number;
  sessionIdentifier?: string;
  authorization?: string;
}) {
  const dispatch = useAppDispatch();
  const { t } = useWebinarT('webinars');
  const content = offer?.contents?.[0];
  if (!offer || !content) {
    return null;
  }

  return (
    <div
      className="p-4 rounded-[20px] border-2 flex flex-col gap-2 items-center justify-center"
      style={{
        backgroundColor:
          content.backgroundColor ||
          (visualIdentity?.theme === 'DARK'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(31, 31, 31, 0.03)'),
        borderColor: content.borderColor || visualIdentity?.contrastColor || '#2C6CFF',
      }}
    >
      <div className="flex items-center gap-2">
        {content?.icon}
        <h2
          style={{
            color: content.titleColor || (visualIdentity?.theme === 'DARK' ? 'white' : 'black'),
          }}
          className={cn(
            'text-[20px]/[24px] font-medium',
            visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black',
          )}
        >
          {content.title}
        </h2>
      </div>
      {content.subTitle && (
        <p
          style={{
            color: content.textColor || (visualIdentity?.theme === 'DARK' ? 'white' : 'black'),
          }}
          className="text-sm text-center"
        >
          {content.subTitle}
        </p>
      )}
      {content.description && (
        <p
          style={{
            color: content.textColor || (visualIdentity?.theme === 'DARK' ? 'white' : 'black'),
          }}
          className="text-xs text-center"
        >
          {content.description}
        </p>
      )}
      {(content.imageUrl || content.imagePath) && (
        <img
          src={content.imageUrl || content.imagePath || ''}
          alt=""
          className="rounded-lg w-full h-auto max-h-56 sm:max-h-64 md:max-h-[332px] object-contain"
        />
      )}
      <Button
        className="w-full sm:w-1/2 font-semibold"
        style={{
          backgroundColor: content.buttonColor || visualIdentity?.contrastColor || '#2C6CFF',
          color: content.buttonTextColor || 'white',
        }}
        onClick={() => {
          if (webinarId && sessionIdentifier && authorization && offer?.id) {
            dispatch(
              postAnalyticsEvent({
                webinarId,
                sessionIdentifier,
                authorization,
                input: {
                  eventType: 'OFFER_CLICK',
                  payloadJson: JSON.stringify({
                    offerId: offer.id,
                  }),
                  occurredAt: new Date().toISOString(),
                },
              }),
            );
          }
          if (content.buttonLink) {
            const url =
              content.buttonLink.startsWith('http://') || content.buttonLink.startsWith('https://')
                ? content.buttonLink
                : `https://${content.buttonLink}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }}
      >
        {content.buttonLabel || t('thankyouPage.defaultBuyButton')}
      </Button>
    </div>
  );
}
